// @ts-check
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { parseISO, isAfter, subDays } from 'date-fns';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
} from 'recharts';

import { TestableCard, TestableSelect, TestableSelectItem, TestableText } from '@components/general/TestableComponents';
import { calculateEMA } from '@components/cards/shared/trends/TrendUtils';
import { RAG_META, RAG_ORDER, formatCurrency } from '../shared/businessViewConfig';

/** Periods the selector offers. Never longer than the API's window (TREND_DAYS), or two
 *  labels would show the same runs and read as a quiet project. */
const TREND_PERIOD_DAYS = [7, 14, 30];

/**
 * The status the trend line tracks. Healthy rather than at risk: it is the figure you want
 * to go up, and being the top stack segment the line sits inside the bars instead of
 * tracing a boundary they already draw - which a line on at risk would, on every run.
 */
const TREND_STATUS = 'healthy';

/** Resolves per colour scheme, so one token covers both. Not the green it tracks: the line
 *  has to stay legible crossing the stack. */
const TREND_COLOUR = 'var(--brand-color)';

/**
 * Tooltip for the trend chart. Written out so the cost each run was incurring is reported
 * alongside the counts - it is the figure the page is about, but money cannot share an axis
 * with flow counts, so this is the only place it appears.
 *
 * @returns {JSX.Element|null}
 */
export const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload ?? {};
  const hasCost = typeof point.dailyRiskCost === 'number';
  const hasTrend = typeof point.trend === 'number';

  return (
    <div
      data-testid="bv-trend-tooltip"
      className="rounded border border-black bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800"
    >
      <div className="px-5 mt-2 font-bold text-gray-900 dark:text-gray-100">{label}</div>
      <div className="px-5 pb-2 border-b border-black text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300">
        {point.completedAt ? new Date(point.completedAt).toLocaleString() : 'Unknown'}
      </div>

      {RAG_ORDER.map((status) => (
        <div key={status} className="flex items-center gap-2 px-5 py-2 text-gray-700 dark:text-gray-300">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: RAG_META[status].hex }} />
          <span className="text-sm">{RAG_META[status].label}</span>
          <span className="ml-auto text-sm font-medium tabular-nums">{point[status] ?? 0}</span>
        </div>
      ))}

      {hasTrend && (
        <div className="flex items-center gap-2 px-5 py-2 border-t border-black text-gray-700 dark:border-gray-600 dark:text-gray-300">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: TREND_COLOUR }} />
          <span className="text-sm">{RAG_META[TREND_STATUS].label} trend</span>
          <span className="ml-auto text-sm font-medium tabular-nums">{point.trend.toFixed(1)}</span>
        </div>
      )}

      {hasCost && (
        <div className="flex items-center gap-2 px-5 py-2 border-t border-gray-400 text-gray-700 dark:border-gray-600 dark:text-gray-300">
          {/* Empty dot so the label lines up with the series rows above. */}
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" />
          <span className="text-sm">Cost incurred</span>
          <span className="ml-auto text-sm font-medium tabular-nums">{formatCurrency(point.dailyRiskCost)}</span>
        </div>
      )}
    </div>
  );
};

TrendTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};

/**
 * Flow status trend: stacked At Risk / Monitor / Healthy counts per run, with the healthy
 * count tracked over them as a smoothed line.
 *
 * Smoothed rather than raw: a single anomalous run would drag a raw line down and straight
 * back up, reading as a collapse and recovery rather than an outlier. Everything shares the
 * one flow-count axis; cost is reported in the tooltip rather than given a second scale.
 *
 * @param {Object} props
 * @param {import('../shared/businessView.types').FlowStatusTrendPoint[]} props.trend
 * @param {number} [props.trendDays=30]
 * @returns {JSX.Element}
 */
const BusinessViewFlowStatusTrend = ({ trend = [], trendDays = 30 }) => {
  const [daysFilter, setDaysFilter] = useState(trendDays);

  /*
    Filters on when each run completed, not on a count of recent runs - "last 7 days" and
    "the last 7 runs" are only the same for a project that runs daily. A run with no
    completedAt is dropped: there is no honest period to place it in.

    calculateEMA is the dashboard's shared trend calculation, so this line means the same
    as the trend lines elsewhere; it writes the smoothed figure onto each point as `trend`.
    Applied after filtering, or narrowing the period leaves the first points carrying runs
    the reader cannot see.
  */
  const data = useMemo(() => {
    const cutoff = subDays(new Date(), daysFilter);

    const withinPeriod = trend.filter((point) => {
      if (!point?.completedAt) return false;

      const completedAt = parseISO(point.completedAt);
      return !Number.isNaN(completedAt.getTime()) && isAfter(completedAt, cutoff);
    });

    return calculateEMA(withinPeriod, TREND_STATUS);
  }, [trend, daysFilter]);

  // The current position is reported from the last calculated run whatever its
  // age, but the trend only covers the API's window — so a project that has
  // been quiet has KPIs and flows to show, and nothing to chart.
  const hasRuns = trend.length > 0;

  // Whether the *selected period* has anything in it, which is not the same thing.
  const hasTrend = data.length > 0;

  return (
    <TestableCard dataTestId="bv-flow-status-trend" className="p-5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <TestableText dataTestId="bv-trend-title" variant="h3" className="uppercase">
            Flow Status Trend
          </TestableText>
          <TestableText dataTestId="bv-trend-subtitle" variant="small" className="mt-1 text-gray-700 dark:text-gray-300">
            Flow status for each run result, with healthy flows trended over them
          </TestableText>
        </div>

        {/*
          Shown whenever the project has runs at all, not only when the selected period
          has any. A period that filters everything out would otherwise take the control
          away with it, leaving no way back to a wider one.
        */}
        {hasRuns && (
          <div className="w-full sm:w-44 shrink-0">
            <TestableSelect
              className="mt-Select-button"
              dataTestId="bv-trend-days-filter"
              value={String(daysFilter)}
              onValueChange={(value) => setDaysFilter(Number(value))}
              aria-label="Trend period"
            >
              {TREND_PERIOD_DAYS.map((days) => (
                <TestableSelectItem
                  dataTestId={`bv-trend-days-${days}`}
                  key={days}
                  className="mt-Select-option"
                  value={String(days)}
                >
                  Last {days} days
                </TestableSelectItem>
              ))}
            </TestableSelect>
          </div>
        )}
      </div>

      {!hasTrend && (
        <TestableText
          dataTestId="bv-trend-empty"
          className="mt-6 pb-2 text-center text-gray-700 dark:text-gray-300"
        >
          {/*
            Names the selected period, not the API window: with the period selector
            still on screen, saying "30 days" while 7 is chosen would read as a
            contradiction of the control right above it.
          */}
          No runs have completed in the last {daysFilter} days, so there is no trend to chart.
          {hasRuns
            ? ' Choose a longer period to see earlier runs.'
            : ' The figures above are from the most recent calculated run.'}
        </TestableText>
      )}

      {hasTrend && (
        <div className="mt-4 h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid stroke="#e6e6e6" vertical={false} />
              <XAxis
                dataKey="run"
                tickLine={false}
                axisLine={{ stroke: '#999' }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={60}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                width={72}
                tickLine={false}
                axisLine={{ stroke: '#999' }}
                allowDecimals={false}
                domain={[0, 'auto']}
                label={{ value: 'Number of flows', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }}
              />
              <Tooltip isAnimationActive={false} cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<TrendTooltip />} />
              <Legend verticalAlign="top" align="center" height={36} />

              {RAG_ORDER.map((status) => (
                <Bar
                  key={status}
                  dataKey={status}
                  name={RAG_META[status].label}
                  stackId="flows"
                  fill={RAG_META[status].hex}
                  legendType="circle"
                  maxBarSize={48}
                />
              ))}

              {/*
                Declared after the bars so it draws on top of them.
              */}
              <Line
                dataKey="trend"
                name={`${RAG_META[TREND_STATUS].label} trend`}
                type="monotone"
                stroke={TREND_COLOUR}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                legendType="line"
                isAnimationActive={false}
                dot={false}
                activeDot={{ r: 5, fill: TREND_COLOUR, stroke: '#ffffff', strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </TestableCard>
  );
};

BusinessViewFlowStatusTrend.propTypes = {
  // Every key but `run` is optional, so the nulls mapTrend produces pass without warning.
  trend: PropTypes.arrayOf(
    PropTypes.shape({
      run: PropTypes.string.isRequired,
      // What the period selector filters on. Null for a run with no completion date.
      completedAt: PropTypes.string,
      at_risk: PropTypes.number,
      monitor: PropTypes.number,
      healthy: PropTypes.number,
      // Null when the run recorded no cost figure. Reported in the tooltip, not plotted.
      dailyRiskCost: PropTypes.number,
    })
  ),
  trendDays: PropTypes.number,
};

export default BusinessViewFlowStatusTrend;
